import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VendorLabel {
  id: string;
  company_id: string;
  name: string;
  color: string | null;
  created_by: string | null;
  created_at: string | null;
}

export interface VendorLabelAssignment {
  id: string;
  company_id: string;
  label_id: string;
  vendor_name: string;
  created_at: string | null;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const LABELS_KEY = ["vendor_labels"] as const;
const ASSIGNMENTS_KEY = ["vendor_label_assignments"] as const;

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

async function fetchLabels(): Promise<VendorLabel[]> {
  const { data, error } = await supabase
    .from("vendor_labels")
    .select("*")
    .order("name", { ascending: true });

  if (error) throw error;
  return data ?? [];
}

async function fetchAssignments(): Promise<VendorLabelAssignment[]> {
  const { data, error } = await supabase
    .from("vendor_label_assignments")
    .select("*")
    .order("vendor_name", { ascending: true });

  if (error) throw error;
  return data ?? [];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Hook for vendor labels (create / rename / delete) and per-vendor assignments. */
export function useVendorLabels() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // ── queries ────────────────────────────────────────────────────────────────

  const {
    data: labels = [],
    isLoading: isLoadingLabels,
    error: labelsError,
  } = useQuery({
    queryKey: LABELS_KEY,
    queryFn: fetchLabels,
  });

  const {
    data: assignments = [],
    isLoading: isLoadingAssignments,
    error: assignmentsError,
  } = useQuery({
    queryKey: ASSIGNMENTS_KEY,
    queryFn: fetchAssignments,
  });

  /** Map of vendor_name → label[] for convenient lookup in UI. */
  const labelsByVendor: Record<string, VendorLabel[]> = {};
  for (const assignment of assignments) {
    const label = labels.find((l) => l.id === assignment.label_id);
    if (!label) continue;
    if (!labelsByVendor[assignment.vendor_name]) {
      labelsByVendor[assignment.vendor_name] = [];
    }
    labelsByVendor[assignment.vendor_name].push(label);
  }

  // ── helpers ────────────────────────────────────────────────────────────────

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: LABELS_KEY });
    queryClient.invalidateQueries({ queryKey: ASSIGNMENTS_KEY });
  }

  // ── createLabel ────────────────────────────────────────────────────────────

  const createLabelMutation = useMutation({
    mutationFn: async ({
      name,
      color,
    }: {
      name: string;
      color?: string | null;
    }): Promise<VendorLabel> => {
      if (!name.trim()) throw new Error("Label name cannot be empty");

      const { data, error } = await supabase
        .from("vendor_labels")
        .insert({ name: name.trim(), color: color ?? null })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LABELS_KEY });
      toast({ title: "Label created" });
    },
    onError: (err) => {
      toast({
        title: "Failed to create label",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  // ── renameLabel ────────────────────────────────────────────────────────────

  const renameLabelMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }): Promise<VendorLabel> => {
      if (!name.trim()) throw new Error("Label name cannot be empty");

      const { data, error } = await supabase
        .from("vendor_labels")
        .update({ name: name.trim() })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LABELS_KEY });
      toast({ title: "Label renamed" });
    },
    onError: (err) => {
      toast({
        title: "Failed to rename label",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  // ── deleteLabel ────────────────────────────────────────────────────────────

  const deleteLabelMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from("vendor_labels").delete().eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      // Deleting a label cascades to assignments — invalidate both.
      invalidate();
      toast({ title: "Label deleted" });
    },
    onError: (err) => {
      toast({
        title: "Failed to delete label",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  // ── assignLabel ────────────────────────────────────────────────────────────

  const assignLabelMutation = useMutation({
    mutationFn: async ({
      vendorName,
      labelId,
    }: {
      vendorName: string;
      labelId: string;
    }): Promise<VendorLabelAssignment> => {
      const { data, error } = await supabase
        .from("vendor_label_assignments")
        .insert({ vendor_name: vendorName, label_id: labelId })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ASSIGNMENTS_KEY });
    },
    onError: (err) => {
      toast({
        title: "Failed to assign label",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  // ── unassignLabel ──────────────────────────────────────────────────────────

  const unassignLabelMutation = useMutation({
    mutationFn: async ({
      vendorName,
      labelId,
    }: {
      vendorName: string;
      labelId: string;
    }): Promise<void> => {
      const { error } = await supabase
        .from("vendor_label_assignments")
        .delete()
        .eq("vendor_name", vendorName)
        .eq("label_id", labelId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ASSIGNMENTS_KEY });
    },
    onError: (err) => {
      toast({
        title: "Failed to remove label",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  // ── return ─────────────────────────────────────────────────────────────────

  return {
    /** All labels for the current company. */
    labels,
    isLoadingLabels,
    labelsError,

    /** All assignments for the current company. */
    assignments,
    isLoadingAssignments,
    assignmentsError,

    /** Convenience map: vendor_name → VendorLabel[]. */
    labelsByVendor,

    /** Create a new label (name required, color optional). */
    createLabel: (name: string, color?: string | null) =>
      createLabelMutation.mutateAsync({ name, color }),
    isCreating: createLabelMutation.isPending,

    /** Rename an existing label by id. */
    renameLabel: (id: string, name: string) => renameLabelMutation.mutateAsync({ id, name }),
    isRenaming: renameLabelMutation.isPending,

    /** Delete a label by id (cascades to assignments). */
    deleteLabel: (id: string) => deleteLabelMutation.mutateAsync(id),
    isDeleting: deleteLabelMutation.isPending,

    /** Assign a label to a vendor. */
    assignLabel: (vendorName: string, labelId: string) =>
      assignLabelMutation.mutateAsync({ vendorName, labelId }),
    isAssigning: assignLabelMutation.isPending,

    /** Remove a label from a vendor. */
    unassignLabel: (vendorName: string, labelId: string) =>
      unassignLabelMutation.mutateAsync({ vendorName, labelId }),
    isUnassigning: unassignLabelMutation.isPending,
  };
}
